import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Printer, FileDown, ImageIcon, Mail, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, todayISO } from "@/lib/format";
import { exportPDF, exportImage, pdfBlob } from "@/lib/exporters";
import { filterText, specialSections } from "@/lib/specialReport";
import { ReportTable } from "@/components/ReportTable";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function EmailDialog({ open, onClose, coll, view, title, subtitle, sections }) {
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries({ collection_id: coll.id, view, to, note }).forEach(([k, v]) => fd.append(k, v));
      fd.append("pdf", pdfBlob(title, subtitle, sections), "Special-Collection-Report.pdf");
      const r = (await api.post("/special-collections/report/email", fd)).data;
      toast.success(`Report emailed to ${r.to.join(", ")}`);
      if (r.failed.length) toast.error(`Could not send to ${r.failed.join(", ")}`);
      setNote(""); onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="special-email-dialog">
        <DialogHeader><DialogTitle>Email this report</DialogTitle><DialogDescription>Each person gets an email with a secure Download PDF button (link works for 30 days).</DialogDescription></DialogHeader>
        <form onSubmit={send} className="space-y-3">
          <label className="block text-sm font-semibold">To (up to 5, separated by commas)<Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.org" className="mt-1 h-11" data-testid="special-email-to" /></label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Short note (optional)" data-testid="special-email-note" />
          <Button type="submit" className="w-full" disabled={busy || !to.trim()} data-testid="special-email-send">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Send</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function SpecialReport({ coll, view, members, filters, admin, onBack }) {
  const ref = useRef(null);
  const [email, setEmail] = useState(false);
  const label = view === "details" ? "Details" : "Summary";
  const title = `${coll.destination} Special Collection — ${coll.name}`;
  const showing = filterText(filters);
  const subtitle = `${label} · Generated ${fmtDate(todayISO())} · ${showing}`;
  const sections = specialSections(view, members);
  const file = `${coll.name.replace(/[^\w]+/g, "-")}-${label}`;
  const image = (type) => exportImage(ref.current, `${file}.${type === "jpeg" ? "jpg" : "png"}`, type).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="print-area space-y-4" data-testid="special-report">
      <div className="no-print flex flex-wrap gap-2">
        <Button variant="outline" onClick={onBack} data-testid="special-report-back"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="special-report-print"><Printer className="mr-2 h-4 w-4" /> Print</Button>
        <Button variant="outline" onClick={() => exportPDF(`${file}.pdf`, title, subtitle, sections)} data-testid="special-report-pdf"><FileDown className="mr-2 h-4 w-4" /> PDF</Button>
        <Button variant="outline" onClick={() => image("jpeg")} data-testid="special-report-jpeg"><ImageIcon className="mr-2 h-4 w-4" /> JPEG</Button>
        <Button variant="outline" onClick={() => image("png")} data-testid="special-report-png"><ImageIcon className="mr-2 h-4 w-4" /> PNG</Button>
        {admin && <Button onClick={() => setEmail(true)} data-testid="special-report-email"><Mail className="mr-2 h-4 w-4" /> Email</Button>}
      </div>
      <div ref={ref} data-annual-doc className="annual-doc rounded-xl bg-white p-6 text-slate-900 shadow-sm sm:p-10" data-testid="special-report-doc">
        <div className="flex flex-col gap-4 border-b-2 border-slate-800 pb-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <Logo className="h-16 w-16 border border-slate-200" />
            <div className="pt-1">
              <div className="font-heading text-2xl font-extrabold leading-normal text-slate-900" data-testid="special-report-title">{title}</div>
              <div className="text-sm text-slate-600">Midwest Jitõbõn Kõmaroñroñ · {label} report</div>
              <div className="mt-1 text-sm font-bold text-slate-900" data-testid="special-report-showing">{showing}</div>
            </div>
          </div>
          <div className="text-sm text-slate-600 sm:text-right">
            <div><span className="font-semibold">Collection:</span> {coll.name}</div>
            <div><span className="font-semibold">People:</span> {members.length}</div>
            <div><span className="font-semibold">Generated:</span> {fmtDate(todayISO())}</div>
          </div>
        </div>
        <div className="mt-6 space-y-8">{sections.map((s, i) => <ReportTable key={s.title} section={s} testid={`special-report-section-${i}`} />)}</div>
      </div>
      {admin && <EmailDialog open={email} onClose={() => setEmail(false)} coll={coll} view={view} title={title} subtitle={subtitle} sections={sections} />}
    </div>
  );
}
